import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "./AuthContext";
import {
  getUsersForManagement,
  getAllOrganizations,
  sendInviteEmail,
  promoteUserToAdmin,
  deactivateUser,
  reactivateUser,
  deleteUserInvite,
} from "./firestoreService";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Modal,
  Chip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Helper to format dates (remains unchanged)
const formatDate = (timestamp) => {
  if (!timestamp || !timestamp.seconds) return "N/A";
  return new Date(timestamp.seconds * 1000).toLocaleDateString();
};

// Style for MUI Modals
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

function UserManagement() {
  const { userRole } = useAuth();

  // All existing state variables are correct and retained
  const [allUsers, setAllUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    name: "",
    status: "all",
    organization: "all",
    role: "all",
  });
  const [selectedUser, setSelectedUser] = useState(null);

  const { t } = useTranslation();

  // --- ✅ THIS IS THE FIX ---
  // The logic inside fetchAllData is updated to correctly handle the new data structure.
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The call to getUsersForManagement now returns an object { users: [], pendingInviteCount: X }
      const [managementData, orgs] = await Promise.all([
        getUsersForManagement(),
        userRole === "superuser" ? getAllOrganizations() : Promise.resolve([]),
      ]);

      // We extract the 'users' array from the returned object before setting the state.
      setAllUsers(managementData.users);

      if (userRole === "superuser") {
        setOrganizations(orgs.filter((org) => org.id !== "dd_quick_platform"));
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
      setError(t("userMgmt.fetchError"));
    } finally {
      setLoading(false);
    }
  }, [userRole, t]);

  useEffect(() => {
    if (userRole) {
      fetchAllData();
    }
  }, [userRole, fetchAllData]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const filteredUsers = useMemo(() => {
    // This line will now work correctly because `allUsers` is guaranteed to be an array.
    return allUsers.filter((user) => {
      const nameMatch = filters.name
        ? (user.displayName || user.email)
            .toLowerCase()
            .includes(filters.name.toLowerCase())
        : true;
      const emailMatch = filters.email
        ? user.email.toLowerCase().includes(filters.email.toLowerCase())
        : true;
      const statusMatch =
        filters.status !== "all" ? user.status === filters.status : true;
      const roleMatch =
        filters.role !== "all"
          ? filters.role === "N/A"
            ? user.role === null
            : user.role === filters.role
          : true;
      const orgMatch =
        userRole === "superuser" && filters.organization !== "all"
          ? user.organizationId === filters.organization
          : true;
      return nameMatch && emailMatch && statusMatch && roleMatch && orgMatch;
    });
  }, [allUsers, filters, userRole]);

  const handleUserAction = async (action, user) => {
    try {
      switch (action) {
        case "promote":
          await promoteUserToAdmin(user.uid);
          break;
        case "deactivate":
          await deactivateUser(user.uid);
          break;
        case "reactivate":
          await reactivateUser(user.uid);
          break;
        case "resend_invite":
          await sendInviteEmail(user.email);
          alert(t("userMgmt.inviteResent"));
          break;
        case "delete_invite":
          // --- ✅ THIS IS THE FIX ---
          if (
            window.confirm(
              t("userMgmt.confirmDeleteInvite", { email: user.email })
            )
          ) {
            // The user.uid for an invite is "invite_DOCUMENT_ID"
            // We need to extract the actual document ID.
            const inviteId = user.uid.startsWith("invite_")
              ? user.uid.split("_")[1]
              : null;
            if (inviteId) {
              await deleteUserInvite(inviteId);
              alert(t("userMgmt.inviteDeleted"));
            } else {
              throw new Error("Could not determine the invite ID.");
            }
          }
          break;
        default:
          throw new Error("Unknown action");
      }
      setSelectedUser(null);
      fetchAllData();
    } catch (err) {
      alert(`Action failed: ${err.message}`);
    }
  };

  // The rest of the file is unchanged and correct...
  if (loading)
    return <CircularProgress sx={{ display: "block", margin: "2rem auto" }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const getStatusChipColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "pending_activation":
        return "warning";
      case "inactive":
        return "error";
      case "invited":
      default:
        return "default";
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("userMgmt.title")}
      </Typography>
      <Paper elevation={2}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              {/* Filter Row */}
              <TableRow>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    name="name"
                    placeholder={t("userMgmt.filterByName")}
                    value={filters.name}
                    onChange={handleFilterChange}
                  />
                </TableCell>
                {userRole === "superuser" && (
                  <TableCell>
                    <FormControl fullWidth variant="standard">
                      <InputLabel>{t("common.organization")}</InputLabel>
                      <Select
                        name="organization"
                        value={filters.organization}
                        onChange={handleFilterChange}
                      >
                        <MenuItem value="all">
                          {t("findTest.allOrganizations")}
                        </MenuItem>
                        {organizations.map((org) => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.shortName || org.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                )}
                <TableCell>
                  <FormControl fullWidth variant="standard">
                    <InputLabel>{t("common.status")}</InputLabel>
                    <Select
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                    >
                      <MenuItem value="all">
                        {t("userMgmt.allStatuses")}
                      </MenuItem>
                      <MenuItem value="invited">
                        {t("userMgmt.invited")}
                      </MenuItem>
                      <MenuItem value="pending_activation">
                        {t("userMgmt.pendingActivation")}
                      </MenuItem>
                      <MenuItem value="active">{t("userMgmt.active")}</MenuItem>
                      <MenuItem value="inactive">
                        {t("userMgmt.inactive")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <FormControl fullWidth variant="standard">
                    <InputLabel>{t("common.role")}</InputLabel>
                    <Select
                      name="role"
                      value={filters.role}
                      onChange={handleFilterChange}
                    >
                      <MenuItem value="all">{t("userMgmt.allRoles")}</MenuItem>
                      <MenuItem value="admin">{t("userMgmt.admin")}</MenuItem>
                      <MenuItem value="user">{t("userMgmt.user")}</MenuItem>
                      <MenuItem value="pro_user">
                        {t("userMgmt.proUser")}
                      </MenuItem>
                      <MenuItem value="N/A">N/A</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>{t("userMgmt.createdInvited")}</TableCell>
              </TableRow>
              {/* Header Row */}
              <TableRow>
                <TableCell>{t("userMgmt.nameEmail")}</TableCell>
                {userRole === "superuser" && (
                  <TableCell>{t("common.organization")}</TableCell>
                )}
                <TableCell>{t("common.status")}</TableCell>
                <TableCell>{t("common.role")}</TableCell>
                <TableCell>{t("userMgmt.createdInvited")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <TableRow
                    key={user.uid}
                    hover
                    onClick={() => setSelectedUser(user)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography variant="body1" component="div">
                        {user.displayName || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </TableCell>
                    {userRole === "superuser" && (
                      <TableCell>
                        {organizations.find((o) => o.id === user.organizationId)
                          ?.shortName || "N/A"}
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={user.status.replace("_", " ")}
                        color={getStatusChipColor(user.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {user.role || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatDate(user.createdAt || user.sentAt)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={userRole === "superuser" ? 5 : 4}
                    align="center"
                  >
                    <Typography
                      color="text.secondary"
                      sx={{ fontStyle: "italic", p: 3 }}
                    >
                      {t("userMgmt.noMatch")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Main User Action Modal */}
      <Modal
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
      >
        <Box sx={modalStyle}>
          <IconButton
            onClick={() => setSelectedUser(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2">
            {t(
              selectedUser?.status === "invited"
                ? "userMgmt.manageInvitation"
                : "userMgmt.manageUser"
            )}
          </Typography>
          <Box mt={2}>
            <Typography>
              <strong>{t("common.email")}:</strong> {selectedUser?.email}
            </Typography>
            {selectedUser?.displayName && (
              <Typography>
                <strong>{t("common.name")}:</strong> {selectedUser?.displayName}
              </Typography>
            )}
            <Typography>
              <strong>{t("common.status")}:</strong> {selectedUser?.status}
            </Typography>
            {selectedUser?.role && (
              <Typography>
                <strong>{t("common.role")}:</strong> {selectedUser?.role}
              </Typography>
            )}
          </Box>
          <Box
            mt={3}
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            {selectedUser?.status === "invited" && (
              <>
                <Button
                  onClick={() =>
                    handleUserAction("resend_invite", selectedUser)
                  }
                >
                  {t("userMgmt.resend")}
                </Button>
                <Button
                  color="error"
                  onClick={() =>
                    handleUserAction("delete_invite", selectedUser)
                  }
                >
                  {t("common.delete")}
                </Button>
              </>
            )}
            {selectedUser?.status === "active" && (
              <>
                <Button
                  onClick={() => handleUserAction("promote", selectedUser)}
                  disabled={selectedUser.role === "admin"}
                >
                  {t("userMgmt.makeAdmin")}
                </Button>
                <Button
                  color="error"
                  onClick={() => handleUserAction("deactivate", selectedUser)}
                >
                  {t("userMgmt.deactivate")}
                </Button>
              </>
            )}
            {selectedUser?.status === "inactive" && (
              <Button
                onClick={() => handleUserAction("reactivate", selectedUser)}
              >
                {t("userMgmt.reactivate")}
              </Button>
            )}
          </Box>
        </Box>
      </Modal>
    </Container>
  );
}

export default UserManagement;
